import React from 'react';

/**
 * Small info icon with a single styled hover tooltip.
 *
 * Extracted from PeopleManager.js (2026-08-02) when the crew-grade access-rights
 * field in BudgetCostCodes.js needed the same affordance — per the Code
 * Unification Principle in CLAUDE.md, shared before it was copied.
 *
 * No native `title` attribute on the icon: that renders a second browser tooltip
 * overlapping this one. The `title` prop is the plain-text fallback shown inside
 * the bubble when no rich `children` are provided.
 *
 * ── Relocated from the root `CLAUDE.md` Shared Modules bullet, 2026-09-11,
 * verbatim; that bullet is now a one-line pointer to this docblock. ──
 *
 * THE field-help affordance: a ⓘ icon plus a `group-hover` bubble.
 * `InfoTip({ title, children })`, renders **`children || title`, never both**
 * (`title` plain-text, `children` rich JSX). Shared since 2026-08-02, indexed
 * 2026-08-10. Call sites all sit on form-field labels; copy the idiom at any
 * `<InfoTip` in `PeopleManager.js` —
 * `<label className="… flex items-center gap-1">{LABEL}<InfoTip title={…} /></label>`
 * (`flex items-center gap-1`, not `block`). 🚨 **NEVER a native `title=` on the
 * icon** (a second browser tooltip over the bubble is the exact defect),
 * 🚨 **and never nest it inside a `<label>` wrapping a checkbox/radio** — the
 * icon's wrapper `<span>` is not `pointer-events-none`, so clicking the ⓘ
 * toggles the control; `OrderForm.js`'s `Exclusive Supplier` is the worked
 * example (the ⓘ is a SIBLING of the label in a flex row, hence the label's
 * `h-full`). ⚠️ HOVER-ONLY, no `aria-describedby`/`role="tooltip"`/focus/tap
 * path; the bubble text is always in the DOM (merely `hidden`), so a screen
 * reader reads it with the label — its only accessibility channel. Pinned by
 * `InfoTip.test.js` (align, `label` mode, icon-mode byte-identity); extend it
 * there. (This said "no test file of its own" until 2026-09-29 — false since
 * the `align` tests of 2026-09-24.)
 * 🪤 The bubble is `bg-gray-900 text-gray-300` with no `dark:` pair deliberately
 * (dark in both themes); neither token is in `index.css`'s `!important` table,
 * so nothing silently beats it
 *
 * `align` (2026-09-24): 'left' (default, byte-identical) hangs the bubble from the
 * icon's LEFT edge; 'right' hangs it from the RIGHT edge so an ⓘ at the right
 * end of a row (the Add-person actions) does not push a 256px bubble off-screen —
 * measured at 1440: +167px page overflow with 'left'.
 */
// `label` (2026-09-29, owner on the gag panel: "I like the tool tips but dont
// like the info symbol"): when given, the caption TEXT is the hover target in
// place of the ⓘ — same bubble, dotted underline as the only cue. Absent, the
// icon renders exactly as before.
export default function InfoTip({ title, children, align = 'left', label = null }) {
  return (
    <span className="relative inline-flex items-center group align-middle">
      {label != null ? (
        <span className="cursor-help underline decoration-dotted decoration-gray-300 dark:decoration-gray-600 underline-offset-2">{label}</span>
      ) : (
      <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-help" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zM11 7a1 1 0 11-2 0 1 1 0 012 0zm-1 2a1 1 0 00-1 1v3a1 1 0 002 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      )}
      {/* 📱 Below md the bubble is `fixed` and spans the screen width minus
          16px each side (owner 2026-10-03: the Supplier "Exclusive" tip ran
          off a phone screen). `top-auto` keeps it at its static position,
          just under the icon; desktop keeps the anchored 256px bubble. */}
      <span className={`pointer-events-none absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-50 hidden group-hover:block w-64 max-md:fixed max-md:top-auto max-md:mt-5 max-md:translate-y-6 max-md:left-4 max-md:right-4 max-md:w-auto rounded-md bg-gray-900 text-gray-300 text-[13px] font-normal leading-snug p-2 shadow-xl normal-case whitespace-normal`}>
        {children || title}
      </span>
    </span>
  );
}
